import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Chip, Container, CssBaseline, Stack, Typography } from '@mui/material';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { Amplify } from 'aws-amplify';
import '@aws-amplify/ui-react-liveness/styles.css';
import { submitLivenessWebhook } from './api/client';
import { getRuntimeConfig } from './runtimeConfig';

export default function App() {
  const url = new URL(window.location.href);
  const tenantId = url.searchParams.get('tenantId') || '';
  const sessionId = url.searchParams.get('sessionId') || '';
  const externalSessionId = url.searchParams.get('externalSessionId') || '';
  const config = getRuntimeConfig();
  const [identityPoolId, setIdentityPoolId] = useState(config.identityPoolId);
  const [rekognitionRegion, setRekognitionRegion] = useState(config.region);
  const [sessionState, setSessionState] = useState<'idle' | 'in_progress' | 'completed' | 'failed' | 'expired'>('idle');
  const [error, setError] = useState('');
  const [startedNotified, setStartedNotified] = useState(false);
  const [completedMessage, setCompletedMessage] = useState('');
  const isTerminal = sessionState === 'completed' || sessionState === 'failed' || sessionState === 'expired';

  const missingRequired = !tenantId || !sessionId || !externalSessionId;
  const missingIdentityPool = !identityPoolId;
  const showDetector = !isTerminal && (sessionState === 'idle' || sessionState === 'in_progress');

  useEffect(() => {
    if (!config.apiBaseUrl || identityPoolId) {
      return;
    }

    let cancelled = false;
    const fetchAuthConfig = async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl.replace(/\/$/, '')}/v1/auth/config`);
        if (!response.ok) {
          throw new Error(`Auth config fetch failed with status ${response.status}.`);
        }
        const payload = await response.json() as { livenessIdentityPoolId?: string; rekognitionRegion?: string };
        if (!cancelled && payload.livenessIdentityPoolId) {
          setIdentityPoolId(payload.livenessIdentityPoolId);
        }
        if (!cancelled && payload.rekognitionRegion) {
          setRekognitionRegion(payload.rekognitionRegion);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError instanceof Error ? fetchError.message : 'Failed to load auth configuration.');
        }
      }
    };

    void fetchAuthConfig();
    return () => {
      cancelled = true;
    };
  }, [config.apiBaseUrl, identityPoolId]);

  useEffect(() => {
    if (missingIdentityPool) {
      return;
    }

    Amplify.configure({
      Auth: {
        Cognito: {
          identityPoolId,
          allowGuestAccess: true,
        },
      },
    });
  }, [identityPoolId, missingIdentityPool]);

  const postStatus = useCallback(async (
    status: 'in_progress' | 'completed' | 'failed' | 'expired',
    failureReason?: string,
    providerPayload?: Record<string, unknown>
  ) => {
    await submitLivenessWebhook({
      tenantId,
      sessionId,
      externalSessionId,
      status,
      failureReason,
      providerPayload: {
        source: 'liveness',
        submittedAt: new Date().toISOString(),
        ...(providerPayload || {}),
      },
    });
  }, [externalSessionId, sessionId, tenantId]);

  useEffect(() => {
    if (missingRequired || startedNotified) {
      return;
    }
    void postStatus('in_progress').then(() => {
      setSessionState('in_progress');
      setStartedNotified(true);
    }).catch((submitError) => {
      setError(submitError instanceof Error ? submitError.message : 'Failed to persist session start.');
    });
  }, [missingRequired, postStatus, startedNotified]);

  const notifyParent = useCallback((status: 'completed' | 'failed' | 'expired') => {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            type: 'privaface.liveness.completed',
            tenantId,
            sessionId,
            status,
          },
          '*'
        );
      }
    } catch {
      // Best-effort only.
    }
  }, [sessionId, tenantId]);

  return (
    <>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', py: 6, background: '#f3f6f8' }}>
        <Container maxWidth="sm">
          <Stack spacing={3}>
            {!isTerminal ? (
              <>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>PrivaFace Liveness</Typography>
                <Typography color="text.secondary">
                  Stage: {config.stage} | Region: {rekognitionRegion}
                </Typography>
                <Chip
                  label={sessionState === 'idle' ? 'ready' : sessionState.replace('_', ' ')}
                  color="default"
                  sx={{ width: 'fit-content' }}
                />
              </>
            ) : null}

            {missingRequired ? (
              <Alert severity="error">
                Missing required query parameters. Expected tenantId, sessionId, and externalSessionId.
              </Alert>
            ) : missingIdentityPool ? (
              <Alert severity="error">
                Missing runtime identityPoolId. Configure it in liveness runtime-config.
              </Alert>
            ) : showDetector ? (
              <FaceLivenessDetector
                sessionId={externalSessionId}
                region={rekognitionRegion}
                onAnalysisComplete={async (deviceInfo) => {
                  setSessionState('in_progress');
                  setError('');
                  try {
                    await postStatus('completed', undefined, {
                      deviceInfo,
                    });
                    setSessionState('completed');
                    setCompletedMessage('Thank you. Your liveness check is complete.');
                    notifyParent('completed');
                  } catch (submitError) {
                    setSessionState('failed');
                    setError(submitError instanceof Error ? submitError.message : 'Failed to persist completed session.');
                  }
                }}
                onUserCancel={() => {
                  setSessionState('expired');
                  setCompletedMessage('Session cancelled. You can close this page.');
                  void postStatus('expired', 'User cancelled liveness session.').catch((submitError) => {
                    setError(submitError instanceof Error ? submitError.message : 'Failed to persist cancelled session.');
                  });
                  notifyParent('expired');
                }}
                onError={(livenessError, deviceInfo) => {
                  setSessionState('failed');
                  const reason = livenessError?.error?.message || 'Liveness check failed.';
                  setError(reason);
                  setCompletedMessage('Liveness failed. You can close this page and try again from a new session link.');
                  void postStatus('failed', reason, { deviceInfo }).catch((submitError) => {
                    setError(submitError instanceof Error ? submitError.message : 'Failed to persist failed session.');
                  });
                  notifyParent('failed');
                }}
              />
            ) : isTerminal ? (
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  minHeight: 320,
                }}
              >
                <Stack
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    width: '100%',
                    maxWidth: 420,
                    px: 3,
                    py: 3.5,
                    borderRadius: 3,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                  }}
                >
                  <Box
                    component="img"
                    src="/branding/privaface-logo-mark.svg"
                    alt="PrivaFace"
                    sx={{ width: 52, height: 52 }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>PrivaFace</Typography>
                  <Typography variant="body2" color="text.secondary">Liveness</Typography>
                  <Chip
                    label={sessionState.replace('_', ' ')}
                    color={sessionState === 'completed' ? 'success' : sessionState === 'failed' ? 'error' : 'default'}
                    sx={{ mt: 0.5 }}
                  />
                  <Typography align="center" sx={{ mt: 1 }}>
                    {sessionState === 'completed'
                      ? 'Thank you. Your liveness session is complete.'
                      : sessionState === 'failed'
                        ? 'Liveness failed. Please request a new link and try again.'
                        : 'Session cancelled. You can close this page.'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    You may now close this page.
                  </Typography>
                  {error ? (
                    <Alert severity="error" sx={{ mt: 1, width: '100%' }}>
                      {error}
                    </Alert>
                  ) : null}
                </Stack>
              </Box>
            ) : null}
            {!isTerminal && error ? <Alert severity="error">{error}</Alert> : null}
            {!isTerminal && completedMessage ? <Alert severity="info">{completedMessage}</Alert> : null}
          </Stack>
        </Container>
      </Box>
    </>
  );
}
